import React, { useEffect, useMemo, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { motion, useReducedMotion } from "framer-motion";

const PRIMARY = "#0c513f";
const ACCENT = "#ffbf07";
const CREAM = "#fdf7e9";
const INK = "#101010";

function Footer() {
  const reduceMotion = useReducedMotion();

  const [showTop, setShowTop] = useState(false);
  const [progress, setProgress] = useState(0);

  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      const height =
        (document.documentElement.scrollHeight || 0) - (window.innerHeight || 0);
      const p = height > 0 ? Math.min(1, Math.max(0, y / height)) : 0;

      setProgress(p);
      setShowTop(y > 260);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const year = useMemo(() => new Date().getFullYear(), []);

  const ringCirc = 2 * Math.PI * 18; // r=18
  const dashOffset = ringCirc * (1 - progress);

  const fadeUp = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 14 },
        whileInView: { opacity: 1, y: 0 },
        transition: { duration: 0.6, ease: "easeOut" },
        viewport: { once: false, amount: 0.25 },
      };

  return (
    <>
      <footer
        id="contact"
        style={{
          position: "relative",
          overflow: "hidden",
          padding: "70px 0 30px",
          background: `linear-gradient(180deg, ${CREAM}, #ffffff)`,
          borderTop: "1px solid rgba(255,191,7,0.22)",
        }}
      >
        {/* soft decor blobs */}
        <div aria-hidden="true" style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          <div
            style={{
              position: "absolute",
              right: "-220px",
              top: "-240px",
              width: 620,
              height: 620,
              borderRadius: 999,
              background: "rgba(255,191,7,0.18)",
              filter: "blur(95px)",
            }}
          />
          <div
            style={{
              position: "absolute",
              left: "-260px",
              bottom: "-260px",
              width: 720,
              height: 720,
              borderRadius: 999,
              background: "rgba(12,81,63,0.10)",
              filter: "blur(105px)",
            }}
          />
        </div>

        <Container style={{ position: "relative", zIndex: 2 }}>
          {/* top mini header */}
          <Row className="justify-content-center mb-4">
            <Col lg={10} className="text-center">
              <motion.div {...fadeUp}>
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 14px",
                    borderRadius: 999,
                    background: "rgba(12,81,63,0.06)",
                    border: "1px solid rgba(12,81,63,0.10)",
                  }}
                >
                  <span
                    style={{
                      width: 9,
                      height: 9,
                      borderRadius: 999,
                      background: ACCENT,
                      boxShadow: "0 0 16px rgba(255,191,7,0.55)",
                    }}
                  />
                  <span style={{ color: PRIMARY, fontWeight: 800, fontSize: 13 }}>
                    Need help? We reply fast.
                  </span>
                </div>

                <h4 style={{ marginTop: 14, marginBottom: 8, color: INK, fontWeight: 900 }}>
                  Let’s keep you well-fed 😌
                </h4>

                <p style={{ margin: 0, color: "rgba(16,16,16,0.64)", maxWidth: 760, marginInline: "auto" }}>
                  Questions, feedback, or partnerships — hit us up anytime.
                </p>
              </motion.div>
            </Col>
          </Row>

          {/* main footer grid */}
          <Row className="justify-content-center g-4">
            {/* Contact */}
            <Col sm={6} lg={4}>
              <motion.div {...fadeUp}>
                <div style={cardStyle()}>
                  <div style={topBarStyle()} />
                  <h5 style={cardTitleStyle()}>Contact</h5>

                  <div style={itemStyle()}>
                    <i className="bi bi-envelope" />
                    <a style={linkStyle()} href="mailto:admin@qwikchow.com">
                      admin@qwikchow.com
                    </a>
                  </div>

                  <div style={itemStyle()}>
                    <i className="bi bi-whatsapp" />
                    <a
                      style={linkStyle()}
                      href="https://wa.me/2349110053549?text=Hello%20there!"
                      target="_blank"
                      rel="noreferrer"
                    >
                      WhatsApp us
                    </a>
                  </div>

                  <div style={{ ...tinyNoteStyle(), marginTop: 10 }}>
                    We only use your contact to respond — no spam.
                  </div>
                </div>
              </motion.div>
            </Col>

            {/* Working hours */}
            <Col sm={6} lg={4}>
              <motion.div {...fadeUp}>
                <div style={cardStyle()}>
                  <div style={topBarStyle(true)} />
                  <h5 style={cardTitleStyle()}>Working Hours</h5>

                  <div style={pillRowStyle()}>
                    <span style={pillStyle()}>Mon–Sat</span>
                    <span style={pillValueStyle()}>6:00AM – 10:00PM</span>
                  </div>

                  <div style={pillRowStyle()}>
                    <span style={pillStyle({ bg: "rgba(255,191,7,0.12)" })}>Sunday</span>
                    <span style={pillValueStyle()}>12:00PM – 8:00PM</span>
                  </div>

                  <div style={{ ...tinyNoteStyle(), marginTop: 10 }}>
                    Support replies may be slightly slower late-night.
                  </div>
                </div>
              </motion.div>
            </Col>

            {/* Social */}
            <Col sm={6} lg={4}>
              <motion.div {...fadeUp}>
                <div style={cardStyle()}>
                  <div style={topBarStyle(false, true)} />
                  <h5 style={cardTitleStyle()}>Follow Us</h5>

                  <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap", marginTop: 8 }}>
                    <SocialBtn
                      href="https://wa.me/2349110053549?text=Hello%20there!"
                      icon="bi bi-whatsapp"
                      label="WhatsApp"
                      reduceMotion={reduceMotion}
                    />
                    <SocialBtn
                      href="https://twitter.com/qwik_chow"
                      icon="bi bi-twitter"
                      label="Twitter"
                      reduceMotion={reduceMotion}
                    />
                    <SocialBtn
                      href="https://instagram.com/qwik_chow"
                      icon="bi bi-instagram"
                      label="Instagram"
                      reduceMotion={reduceMotion}
                    />
                    <SocialBtn
                      href="mailto:admin@qwikchow.com"
                      icon="bi bi-envelope"
                      label="Email"
                      reduceMotion={reduceMotion}
                    />
                  </div>

                  <div style={{ ...tinyNoteStyle(), marginTop: 10 }}>
                    Tag us in your chow 😄
                  </div>
                </div>
              </motion.div>
            </Col>
          </Row>

          {/* bottom bar */}
          <Row className="justify-content-center mt-4">
            <Col lg={10}>
              <div
                style={{
                  marginTop: 10,
                  paddingTop: 18,
                  borderTop: "1px solid rgba(12,81,63,0.10)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ color: "rgba(16,16,16,0.60)" }}>
                  © {year} <span style={{ color: PRIMARY, fontWeight: 900 }}>Qwik Chow</span>. All rights reserved.
                </div>

                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <a href="#Home" style={{ ...linkStyle(), fontWeight: 800 }}>
                    Home
                  </a>
                  <a href="#features" style={{ ...linkStyle(), fontWeight: 800 }}>
                    Features
                  </a>
                  <a href="#download" style={{ ...linkStyle(), fontWeight: 800 }}>
                    Download
                  </a>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </footer>

      {/* Scroll To Top (with progress ring) */}
      {showTop && (
        <motion.button
          type="button"
          onClick={scrollTop}
          aria-label="Scroll to top"
          initial={reduceMotion ? false : { opacity: 0, y: 14 }}
          animate={reduceMotion ? {} : { opacity: 1, y: 0 }}
          whileHover={reduceMotion ? {} : { scale: 1.06 }}
          whileTap={reduceMotion ? {} : { scale: 0.98 }}
          transition={{ duration: 0.25 }}
          style={{
            position: "fixed",
            right: 18,
            bottom: 18,
            width: 54,
            height: 54,
            borderRadius: 999,
            border: "1px solid rgba(255,191,7,0.35)",
            background: "rgba(255,255,255,0.85)",
            boxShadow: "0 14px 50px rgba(0,0,0,0.14)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            display: "grid",
            placeItems: "center",
            cursor: "pointer",
            zIndex: 9999,
          }}
        >
          <svg width="44" height="44" viewBox="0 0 44 44" style={{ position: "absolute" }}>
            <circle cx="22" cy="22" r="18" stroke="rgba(12,81,63,0.10)" strokeWidth="3" fill="none" />
            <circle
              cx="22"
              cy="22"
              r="18"
              stroke={ACCENT}
              strokeWidth="3"
              fill="none"
              strokeLinecap="round"
              strokeDasharray={ringCirc}
              strokeDashoffset={dashOffset}
              style={{ transition: "stroke-dashoffset 120ms linear" }}
            />
          </svg>

          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 999,
              background: `linear-gradient(135deg, ${PRIMARY}, #0b4a3a)`,
              display: "grid",
              placeItems: "center",
              border: "1px solid rgba(255,191,7,0.35)",
            }}
          >
            <i className="bi bi-arrow-up" style={{ color: "#fff", fontSize: 16, lineHeight: 0 }} />
          </div>
        </motion.button>
      )}
    </>
  );
}

/* ---------- tiny helpers (inline style only; no CSS needed) ---------- */

function cardStyle() {
  return {
    borderRadius: 22,
    background: "rgba(255,255,255,0.78)",
    border: "1px solid rgba(12,81,63,0.10)",
    boxShadow: "0 22px 70px rgba(0,0,0,0.10)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    padding: 18,
    height: "100%",
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
  };
}

function topBarStyle(usePrimary = false, useAccent = false) {
  const a = usePrimary ? PRIMARY : ACCENT;
  const b = useAccent ? ACCENT : PRIMARY;
  return {
    height: 7,
    borderRadius: 999,
    background: `linear-gradient(90deg, ${a}, rgba(255,255,255,0.12), ${b})`,
    opacity: 0.95,
    marginBottom: 12,
  };
}

function cardTitleStyle() {
  return { margin: "0 0 10px", fontWeight: 900, color: INK };
}

function itemStyle() {
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    padding: "10px 12px",
    borderRadius: 16,
    background: "rgba(255,191,7,0.10)",
    border: "1px solid rgba(255,191,7,0.22)",
    marginTop: 10,
  };
}

function linkStyle() {
  return {
    color: PRIMARY,
    textDecoration: "none",
    fontWeight: 900,
  };
}

function tinyNoteStyle() {
  return { color: "rgba(16,16,16,0.58)", fontSize: 12.5 };
}

function pillRowStyle() {
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    padding: "10px 12px",
    borderRadius: 16,
    background: "rgba(12,81,63,0.05)",
    border: "1px solid rgba(12,81,63,0.10)",
    marginTop: 10,
  };
}

function pillStyle(opts = {}) {
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "6px 10px",
    borderRadius: 999,
    background: opts.bg || "rgba(255,191,7,0.12)",
    border: "1px solid rgba(255,191,7,0.22)",
    color: PRIMARY,
    fontWeight: 900,
    fontSize: 12.5,
    whiteSpace: "nowrap",
  };
}

function pillValueStyle() {
  return { color: "rgba(16,16,16,0.68)", fontWeight: 800, whiteSpace: "nowrap" };
}

function SocialBtn({ href, icon, label, reduceMotion }) {
  return (
    <motion.a
      href={href}
      target={href.startsWith("mailto:") ? "_self" : "_blank"}
      rel="noreferrer"
      whileHover={reduceMotion ? {} : { y: -3, scale: 1.03 }}
      whileTap={reduceMotion ? {} : { scale: 0.98 }}
      transition={{ duration: 0.2 }}
      style={{
        width: 52,
        height: 52,
        borderRadius: 999,
        display: "grid",
        placeItems: "center",
        background: "rgba(255,255,255,0.75)",
        border: "1px solid rgba(255,191,7,0.30)",
        boxShadow: "0 14px 40px rgba(0,0,0,0.10)",
        color: PRIMARY,
        textDecoration: "none",
        position: "relative",
      }}
      aria-label={label}
      title={label}
    >
      <i className={icon} style={{ fontSize: 18 }} />
    </motion.a>
  );
}

export default Footer;
